/**
 * Hook resolving the **reporting base currency** — the currency a base-currency
 * KPI aggregate is denominated in.
 *
 * It mirrors the backend `FxService.resolve_reporting_base_currency`
 * (active company base → flagged accounting base → tenant default → USD) via the
 * `/tenant/reporting-base-currency` endpoint, scoped by the active company the
 * axios interceptor sends in `X-Active-Company-Id`.
 *
 * This is the anchor for currency-less aggregate totals (dashboard KPIs, report
 * summaries the backend computes in the tenant base). It deliberately does NOT
 * consult `user.preferred_currency`: those totals are never converted at report
 * time (ERP P13), so labelling one with the viewer's preferred symbol would
 * misrepresent an unconverted number. Contrast `useDefaultCurrency`, which IS
 * the viewer's preference and belongs on create-form currency defaults.
 */
import { useQuery } from '@tanstack/react-query';

import { useAuth } from '../auth';
import { tenantSettingsApi } from '../services/tenantSettingsApi';
import { useTenantLocaleSettings } from './useTenantSettings';

export const REPORTING_BASE_CURRENCY_QUERY_KEY = ['reporting-base-currency'];

/**
 * Returns the reporting base currency code (always a string). While the precise
 * value loads it falls back to the tenant default currency, then 'USD' — never
 * the user's preferred currency, so the bug it fixes can't reappear on the
 * loading flash. The query is invalidated with the active-company scope (the
 * CompanyScopeProvider invalidates active queries on scope change).
 */
export function useReportingBaseCurrency(): string {
  const { user } = useAuth();
  const { data: locale } = useTenantLocaleSettings();

  const { data } = useQuery({
    queryKey: REPORTING_BASE_CURRENCY_QUERY_KEY,
    queryFn: tenantSettingsApi.getReportingBaseCurrency,
    enabled: !!user?.tenant_id,
    staleTime: 5 * 60 * 1000,
  });

  return data?.currency_code ?? locale?.default_currency ?? 'USD';
}
